import {
  useState,
  KeyboardEvent,
  useEffect,
  RefObject,
  memo,
  useCallback,
  useMemo,
} from 'react';
import { focusInput, focusInputWithSelection } from '../utils/focus';
import { cn } from './ui/utils';

interface TerminalInputProps {
  onCommand: (command: string, isChatMode?: boolean) => void;
  disabled: boolean;
  inputRef: RefObject<HTMLInputElement | null>;
  chatMode: boolean;
  onToggleChatMode: () => void;
  inputMode: 'normal' | 'email-early-access' | 'email-get-started';
  onExitEmailMode: () => void;
}

export const TerminalInput = memo(function TerminalInput({
  onCommand,
  disabled,
  inputRef,
  chatMode,
  onToggleChatMode,
  inputMode,
  onExitEmailMode,
}: TerminalInputProps) {
  const isEmailMode = inputMode !== 'normal';

  // Helper function to get initial input value
  const getInitialInput = (emailMode: boolean, chat: boolean) => {
    if (emailMode) return '';
    if (chat) return '';
    return 'mifta ';
  };

  const [input, setInput] = useState(() =>
    getInitialInput(isEmailMode, chatMode)
  );

  useEffect(() => {
    // Update input when chat mode or input mode changes
    if (isEmailMode) {
      setInput('');
    } else if (chatMode) {
      setInput('');
    } else {
      setInput('mifta ');
    }
  }, [chatMode, isEmailMode]);

  useEffect(() => {
    // Focus input on mount and position cursor
    if (inputRef.current) {
      inputRef.current.focus();
      if (!chatMode) {
        inputRef.current.setSelectionRange(6, 6); // Position after "mifta "
      }
    }
  }, [inputRef, chatMode]);

  // Helper: Check if input is just "mifta" or "mifta "
  const isJustMifta = useCallback((value: string) => {
    return value.trim() === 'mifta' || value === 'mifta ';
  }, []);

  // Helper: Handle ESC key press
  const handleEscapeKey = useCallback(() => {
    if (isEmailMode) {
      onExitEmailMode();
      setInput('mifta ');
      focusInputWithSelection(inputRef, 6, 0);
      return true;
    }
    if (chatMode) {
      onCommand('exit', true);
      setInput('mifta ');
      focusInputWithSelection(inputRef, 6, 0);
      return true;
    }
    return false;
  }, [isEmailMode, chatMode, onExitEmailMode, onCommand, inputRef]);

  // Helper: Handle email mode submission
  const handleEmailSubmission = useCallback(
    (value: string) => {
      if (isEmailMode && value.trim()) {
        onCommand(value.trim(), false);
        setInput('');
        focusInput(inputRef, 0);
        return true;
      }
      return false;
    },
    [isEmailMode, onCommand, inputRef]
  );

  // Helper: Handle chat mode toggle
  const handleChatModeToggle = useCallback(
    (value: string) => {
      if (!isEmailMode && !chatMode && isJustMifta(value)) {
        onToggleChatMode();
        return true;
      }
      return false;
    },
    [isEmailMode, chatMode, isJustMifta, onToggleChatMode]
  );

  // Helper: Handle chat mode submission
  const handleChatSubmission = useCallback(
    (value: string) => {
      if (chatMode && value.trim()) {
        onCommand(value.trim(), true);
        setInput('');
        focusInput(inputRef, 0);
        return true;
      }
      return false;
    },
    [chatMode, onCommand, inputRef]
  );

  // Helper: Handle command mode submission
  const handleCommandSubmission = useCallback(
    (value: string) => {
      if (
        !chatMode &&
        !isEmailMode &&
        value.trim() &&
        value.trim() !== 'mifta'
      ) {
        const command = value.replace(/^mifta\s+/, '').trim();
        if (command) {
          onCommand(command, false);
          setInput('mifta ');
          focusInputWithSelection(inputRef, 6, 0);
          return true;
        }
      }
      return false;
    },
    [chatMode, isEmailMode, onCommand, inputRef]
  );

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (disabled) return;

      // Handle ESC key - Exit chat mode or email mode
      if (e.key === 'Escape') {
        handleEscapeKey();
        return;
      }

      if (e.key === 'Enter') {
        // Try handlers in order: email, chat toggle, chat submission, command
        if (handleEmailSubmission(input)) return;
        if (handleChatModeToggle(input)) return;
        if (handleChatSubmission(input)) return;
        handleCommandSubmission(input);
      }
    },
    [
      disabled,
      input,
      handleEscapeKey,
      handleEmailSubmission,
      handleChatModeToggle,
      handleChatSubmission,
      handleCommandSubmission,
    ]
  );

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    // Allow user to edit "mifta" part
    setInput(newValue);
  }, []);

  const handleButtonClick = useCallback(() => {
    if (disabled) return;

    // Try handlers in order: email, chat toggle, chat submission, command
    if (handleEmailSubmission(input)) return;
    if (handleChatModeToggle(input)) return;
    if (handleChatSubmission(input)) return;
    handleCommandSubmission(input);
  }, [
    disabled,
    input,
    handleEmailSubmission,
    handleChatModeToggle,
    handleChatSubmission,
    handleCommandSubmission,
  ]);

  // Determine input text color based on mode
  const inputColorClass = useMemo(() => {
    if (isEmailMode) return 'text-cyan-400';
    if (chatMode) return 'text-purple-400';
    if (isJustMifta(input)) return 'text-purple-400';
    return 'text-cyan-400';
  }, [isEmailMode, chatMode, input, isJustMifta]);

  // Get prompt symbol based on mode
  const promptSymbol = useMemo(() => {
    if (isEmailMode) return '@';
    if (chatMode) return '›';
    return '$';
  }, [isEmailMode, chatMode]);

  // Get placeholder text based on mode
  const placeholderText = useMemo(() => {
    if (isEmailMode) return 'your.email@example.com (Press ESC to cancel)';
    if (chatMode) return 'Ask me anything... (Press ESC to exit chat mode)';
    return 'Type a command...';
  }, [isEmailMode, chatMode]);

  // Get button text based on mode
  const buttonText = useMemo(() => {
    if (isEmailMode) return 'Submit';
    if (chatMode) return 'Send';
    return 'Run';
  }, [isEmailMode, chatMode]);

  return (
    <div className="bg-[#0f0f0f] border-t border-[#2a2a2a] px-4 py-3 flex items-center gap-3 font-mono relative">
      <span
        className={cn(
          isEmailMode && 'text-green-400',
          chatMode && 'text-purple-400',
          !isEmailMode && !chatMode && 'text-cyan-400'
        )}
      >
        {promptSymbol}
      </span>
      <div className="flex-1 relative">
        <input
          ref={inputRef}
          type="text"
          value={input}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholderText}
          disabled={disabled}
          className={cn(
            'w-full bg-transparent outline-none placeholder:text-gray-600 disabled:opacity-50',
            inputColorClass
          )}
        />
        {!isEmailMode && !chatMode && isJustMifta(input) && (
          <span className="max-w-[calc(100%-64px)] overflow-hidden absolute left-15 top-1/2 -translate-y-1/2 text-gray-600 pointer-events-none whitespace-nowrap">
            type a command or press ENTER to switch to chat mode
          </span>
        )}
      </div>
      <button
        onClick={handleButtonClick}
        disabled={
          disabled ||
          !input.trim() ||
          (!isEmailMode && !chatMode && isJustMifta(input))
        }
        className="px-4 py-1.5 bg-cyan-600 hover:bg-cyan-700 text-white rounded disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
      >
        {buttonText}
      </button>
    </div>
  );
});
